import { FC, useState, useEffect, useMemo } from "react";
import { useGetActivities } from "~/services/activities";
import { Table, Input, Button, Select, DatePicker, Space, Tag, Card, Checkbox, Modal, Tabs } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { Activity } from "~/services/activities";
import { userService } from "~/services/user/user.service";
import UpcomingServiceTickets from "../inventories/list-inventories/upcoming-service-tickets";
import HistoryServiceTickets from "../inventories/list-inventories/history-service-tickets";
import { useGetInventory } from "~/services/inventory";

const { RangePicker } = DatePicker;

type ActivityLogFilters = {
  page: number;
  limit: number;
  search?: string;
  type?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
};

const activityTypes = [
  { value: "SERVICE_CREATED", label: "Service Created" },
  { value: "SERVICE_UPDATED", label: "Service Updated" },
  { value: "SERVICE_DELETED", label: "Service Deleted" },
  { value: "SERVICE_STATUS_CHANGED", label: "Service Status Changed" },
  { value: "INVENTORY_ADDED", label: "Inventory Added" },
  { value: "INVENTORY_UPDATED", label: "Inventory Updated" },
  { value: "INVENTORY_REMOVED", label: "Inventory Removed" },
  { value: "STAFF_ADDED", label: "Staff Added" },
  { value: "STAFF_REMOVED", label: "Staff Removed" },
  { value: "VESSEL_CHANGED", label: "Vessel Changed" },
  { value: "DOCUMENT_ADDED", label: "Document Added" },
  { value: "DOCUMENT_REMOVED", label: "Document Removed" },
  { value: "NOTE_ADDED", label: "Note Added" },
  { value: "BOOKING_CREATED", label: "Booking Created" },
  { value: "BOOKING_UPDATED", label: "Booking Updated" },
];

// Quick filter presets - exclude inventory activities
const nonInventoryTypes = activityTypes
  .filter((t) => !t.value.includes("INVENTORY"))
  .map((t) => t.value);

const getActivityTypeColor = (type: string): string => {
  if (type.includes("CREATED")) return "green";
  if (type.includes("UPDATED")) return "blue";
  if (type.includes("DELETED") || type.includes("REMOVED")) return "red";
  if (type.includes("ADDED")) return "cyan";
  if (type.includes("CHANGED")) return "orange";
  return "default";
};

export const ActivityLogs: FC = () => {
  const [filters, setFilters] = useState<ActivityLogFilters>({
    page: 1,
    limit: 50,
  });
  const [hideInventory, setHideInventory] = useState(false);
  const [selectedInventoryId, setSelectedInventoryId] = useState<string | null>(null);

  const { data, isLoading } = useGetActivities(filters);
  const { data: selectedInventory } = useGetInventory(selectedInventoryId);

  // Extract unique users from the activity logs
  const users = useMemo(() => {
    if (!data?.items) return [];

    const userMap = new Map<string, { id: string; firstName: string; lastName: string }>();

    data.items.forEach(activity => {
      if (activity.user && activity.user.id) {
        userMap.set(activity.user.id, {
          id: activity.user.id,
          firstName: activity.user.firstName,
          lastName: activity.user.lastName,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    );
  }, [data?.items]);

  // Filter out inventory activities on the client side if hideInventory is true
  const filteredData = hideInventory
    ? {
        ...data,
        items: data?.items?.filter((item) => !item.type.includes("INVENTORY")) || [],
      }
    : data;

  const columns: ColumnsType<Activity> = [
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (createdAt: string) => (
        <div>
          <div className="font-medium">
            {dayjs(createdAt).format("MMM DD, YYYY")}
          </div>
          <div className="text-gray-500 text-sm">
            {dayjs(createdAt).format("h:mm:ss A")}
          </div>
        </div>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 150,
      render: (user: Activity["user"]) => (
        <div className="text-sm">
          {user ? `${user.firstName} ${user.lastName}` : "System"}
        </div>
      ),
    },
    {
      title: "Activity Type",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (type: string) => (
        <Tag color={getActivityTypeColor(type)}>
          {type.replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description: string, record: Activity) => (
        <div className="flex items-center gap-2">
          <span>{description}</span>
          {record.inventoryId && (
            <Button
              type="link"
              size="small"
              className="text-green-500 p-0 h-auto"
              onClick={() => setSelectedInventoryId(record.inventoryId || null)}
            >
              [View Inventory]
            </Button>
          )}
        </div>
      ),
    },
    {
      title: "Service Type",
      dataIndex: "metadata",
      key: "serviceType",
      width: 200,
      render: (metadata: any) => (
        <div className="text-sm">
          {metadata?.serviceType || "-"}
        </div>
      ),
    },
    {
      title: "Job Ticket",
      dataIndex: "jobTicket",
      key: "jobTicket",
      width: 150,
      render: (jobTicket: Activity["jobTicket"], record: Activity) => {
        if (!jobTicket) return "-";

        // Use service ticket serial if available (it includes full ref), otherwise use job ticket ref
        const displayReference = record.serviceTicket?.serial || jobTicket.referenceNumber;

        // Build URL - if there's a service ticket, link directly to it with tab and serviceId params
        const url = record.serviceTicketId
          ? `/bookings/${jobTicket.id}?tab=services&serviceId=${record.serviceTicketId}`
          : `/bookings/${jobTicket.id}`;

        return (
          <a
            href={url}
            className="text-blue-600 hover:underline"
          >
            {displayReference || "View"}
          </a>
        );
      },
    },
  ];

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 50,
    });
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setFilters({
        ...filters,
        startDate: dates[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : undefined,
        endDate: dates[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : undefined,
        page: 1,
      });
    } else {
      setFilters({
        ...filters,
        startDate: undefined,
        endDate: undefined,
        page: 1,
      });
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setFilters({
      ...filters,
      page: pagination.current || 1,
      limit: pagination.pageSize || 50,
    });
  };

  const hasActiveFilters = Boolean(
    filters.search || filters.type || filters.userId || filters.startDate || filters.endDate
  );

  return (
    <div className="space-y-4 max-w-[95vw] mx-auto">
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap>
            <Input
              placeholder="Search description..."
              prefix={<SearchOutlined />}
              value={filters.search || ""}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value, page: 1 })
              }
              style={{ width: 250 }}
              allowClear
            />

            <Select
              placeholder="All Activity Types"
              value={filters.type || undefined}
              onChange={(value) =>
                setFilters({ ...filters, type: value, page: 1 })
              }
              style={{ width: 200 }}
              allowClear
            >
              {activityTypes.map((type) => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="All Users"
              value={filters.userId || undefined}
              onChange={(value) =>
                setFilters({ ...filters, userId: value, page: 1 })
              }
              style={{ width: 200 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={users.map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
              }))}
            />

            <RangePicker
              onChange={handleDateRangeChange}
              format="MMM DD, YYYY"
              value={
                filters.startDate && filters.endDate
                  ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                  : null
              }
            />

            <Checkbox
              checked={hideInventory}
              onChange={(e) => setHideInventory(e.target.checked)}
            >
              Hide Inventory Activities
            </Checkbox>

            {hasActiveFilters && (
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={filteredData?.items || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total: data?.meta?.totalItems || 0,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} activities`,
              pageSizeOptions: ["20", "50", "100"],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1400 }}
          />
        </Space>
      </Card>

      {/* Inventory Detail Modal */}
      <Modal
        title="Inventory Info"
        open={!!selectedInventoryId}
        onCancel={() => setSelectedInventoryId(null)}
        footer={null}
        width={900}
      >
        {selectedInventory && (
          <Tabs
            defaultActiveKey="details"
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-1">Inventory Reference</div>
                        <div className="text-base">{selectedInventory.inventoryRef}</div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-1">Type</div>
                        <div className="text-base">{selectedInventory.itemType}</div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-1">Status</div>
                        <div className="text-base">{selectedInventory.status}</div>
                      </div>

                      {selectedInventory.client && (
                        <div>
                          <div className="text-sm font-semibold text-gray-600 mb-1">Client</div>
                          <div className="text-base">{selectedInventory.client.clientName}</div>
                        </div>
                      )}

                      {selectedInventory.location && (
                        <div>
                          <div className="text-sm font-semibold text-gray-600 mb-1">Location</div>
                          <div className="text-base">{selectedInventory.location.locationName}</div>
                        </div>
                      )}

                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-1">Dimensions</div>
                        <div className="text-base">
                          {selectedInventory.length}" × {selectedInventory.width}" × {selectedInventory.height}"
                        </div>
                      </div>

                      {selectedInventory.itemDescription && (
                        <div className="col-span-2">
                          <div className="text-sm font-semibold text-gray-600 mb-1">Description</div>
                          <div className="text-base">{selectedInventory.itemDescription}</div>
                        </div>
                      )}

                      {selectedInventory.value && (
                        <div>
                          <div className="text-sm font-semibold text-gray-600 mb-1">Value</div>
                          <div className="text-base">
                            {selectedInventory.value ? `$${selectedInventory.value.toLocaleString()}` : 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                key: 'upcoming',
                label: 'Upcoming Service Tickets',
                children: <UpcomingServiceTickets inventoryId={selectedInventory.id} />,
              },
              {
                key: 'history',
                label: 'History',
                children: <HistoryServiceTickets inventoryId={selectedInventory.id} />,
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};
